import { useEffect, useState, useCallback, useRef } from "react";
import { getPaginatedComparisonResearchQueries } from "../services/comparisonResearchQueries";

interface IUseFetchComparisonResearchQueries {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: any;
  pSize: any;
  ruleId?: string;
  comparisonResearchQueryId?: string;
  statusFilter?: string;
}

const useFetchPaginatedComparisonResearchQueries = ({
  setIsLoading,
  page,
  pSize,
  ruleId,
  comparisonResearchQueryId,
  statusFilter,
}: IUseFetchComparisonResearchQueries) => {
  const [comparisonResearchQueries, setComparisonResearchQueries] =
    useState<any>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    try {
      const result = await getPaginatedComparisonResearchQueries({
        page,
        pSize,
        ruleId,
        comparisonResearchQueryId,
        statusFilter,
        signal: abortControllerRef.current.signal,
      });
      setComparisonResearchQueries(result);
    } catch (err: any) {
      // Don't update state if request was aborted
      if (err.name !== "AbortError") {
        setComparisonResearchQueries([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [
    page,
    pSize,
    ruleId,
    comparisonResearchQueryId,
    statusFilter,
    setIsLoading,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [comparisonResearchQueries, fetchData];
};

export default useFetchPaginatedComparisonResearchQueries;
